import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, Check, X, Gavel, Clock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '@/contexts/AuthContext';
import { getPhotoUrl } from '@/utils/photoUrl';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const photoGuide = [
  '1. Frente',
  '2. Dorso',
  '3. Costado derecho',
  '4. Tablero',
  '5. Odómetro',
  '6. Costado izquierdo',
  '7. Interior delantero',
  '8. Interior trasero',
  '9. Motor',
  '10. Baúl/Maletero',
];

const SortablePhoto = ({ url, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-square overflow-hidden rounded-md ring-1 ring-border group"
      data-testid="photo-thumbnail"
    >
      <div {...attributes} {...listeners} className="h-full w-full cursor-grab active:cursor-grabbing">
        <img
          src={getPhotoUrl(url)}
          alt={`Foto ${index + 1}`}
          className="h-full w-full object-cover group-hover:scale-105 transition-transform pointer-events-none"
        />
      </div>
      <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded pointer-events-none">
        {index + 1}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        className="absolute top-1 right-1 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all z-10"
        title="Eliminar foto"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const NuevaPublicacion = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const duplicarId = searchParams.get('duplicar');
  const { user } = useAuth();
  const [step, setStep] = useState('step1');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [sucursales, setSucursales] = useState([]);

  const [formData, setFormData] = useState({
    marca: '',
    modelo: '',
    version: '',
    anio: new Date().getFullYear(),
    km: '',
    tipoCombustible: '',
    cilindrada: '',
    comentarios: '',
    estadoGeneral: '',
    unicoDueno: false,
    precioBase: '',
    fotos: [],
    estadoPublicacion: 'Borrador',
    autor: 'Andrea Pérez',
    esSubasta: false,
    fechaLimiteOfertas: '',
    sucursalId: '',
  });

  const [photoFiles, setPhotoFiles] = useState([]);

  // Cargar sucursales al iniciar
  useEffect(() => {
    loadSucursales();
  }, []);

  // Cargar datos del vehículo si estamos editando
  useEffect(() => {
    if (editId) {
      loadVehiculoData(editId);
    }
  }, [editId]);

  // Cargar datos para duplicar publicación
  useEffect(() => {
    if (duplicarId) {
      loadDuplicateData(duplicarId);
    }
  }, [duplicarId]);

  // Asignar sucursal del usuario automáticamente
  useEffect(() => {
    if (user?.sucursalId && !editId && !duplicarId) {
      setFormData(prev => ({ ...prev, sucursalId: user.sucursalId }));
    }
  }, [user, editId, duplicarId]);

  const loadSucursales = async () => {
    try {
      const response = await axios.get(`${API}/sucursales`);
      setSucursales(response.data.filter(s => s.activa));
    } catch (error) {
      console.error('Error loading sucursales:', error);
    }
  };

  const loadVehiculoData = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehiculos/${id}`);
      const vehiculo = response.data;

      // Cargar datos en el formulario
      setFormData({
        marca: vehiculo.marca || '',
        modelo: vehiculo.modelo || '',
        version: vehiculo.version || '',
        anio: vehiculo.anio || new Date().getFullYear(),
        km: vehiculo.km || '',
        tipoCombustible: vehiculo.tipoCombustible || '',
        cilindrada: vehiculo.cilindrada || '',
        comentarios: vehiculo.comentarios || '',
        estadoGeneral: vehiculo.estadoGeneral || '',
        unicoDueno: vehiculo.unicoDueno || false,
        precioBase: vehiculo.precioBase || '',
        fotos: vehiculo.fotos || [],
        estadoPublicacion: vehiculo.estadoPublicacion || 'Borrador',
        autor: vehiculo.autor || 'Andrea Pérez',
        esSubasta: vehiculo.esSubasta || false,
        fechaLimiteOfertas: vehiculo.fechaLimiteOfertas ? vehiculo.fechaLimiteOfertas.slice(0, 16) : '',
        sucursalId: vehiculo.sucursalId || '',
      });

      toast.success('Datos cargados para edición');
    } catch (error) {
      console.error('Error loading vehiculo:', error);
      toast.error('Error al cargar los datos del vehículo');
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicateData = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehiculos/${id}`);
      const vehiculo = response.data;

      setFormData({
        marca: vehiculo.marca || '',
        modelo: vehiculo.modelo || '',
        version: vehiculo.version || '',
        anio: vehiculo.anio || new Date().getFullYear(),
        km: vehiculo.km || '',
        tipoCombustible: vehiculo.tipoCombustible || '',
        cilindrada: vehiculo.cilindrada || '',
        comentarios: vehiculo.comentarios || '',
        estadoGeneral: vehiculo.estadoGeneral || '',
        unicoDueno: vehiculo.unicoDueno || false,
        precioBase: vehiculo.precioBase || '',
        fotos: vehiculo.fotos || [],
        estadoPublicacion: 'Borrador',
        autor: user?.nombre || vehiculo.autor || '',
        esSubasta: vehiculo.esSubasta || false,
        fechaLimiteOfertas: '',
        sucursalId: vehiculo.sucursalId || user?.sucursalId || '',
      });

      toast.success('Datos cargados para nueva publicación');
    } catch (error) {
      console.error('Error loading vehiculo for duplication:', error);
      toast.error('Error al cargar los datos del vehículo');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    // Filter out files that are already uploaded to avoid duplicates if re-dropping
    const newFiles = acceptedFiles.slice(0, 20 - formData.fotos.length);
    if (newFiles.length === 0) return;

    setUploadingImages(true);
    const uploadFormData = new FormData();
    newFiles.forEach(file => {
      uploadFormData.append('files', file);
    });

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Backend returns filenames e.g. a1b2c3d4.jpg
      const uploadedUrls = response.data.urls;

      setFormData(prev => ({
        ...prev,
        fotos: [...prev.fotos, ...uploadedUrls]
      }));

      toast.success(`${newFiles.length} foto(s) subida(s) exitosamente`);
    } catch (error) {
      console.error('Error uploading images:', error);
      const errorMsg = error.response?.data?.detail || 'Error al subir las imágenes';
      toast.error(errorMsg);
    } finally {
      setUploadingImages(false);
    }
  };

  const removePhoto = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      fotos: prev.fotos.filter((_, index) => index !== indexToRemove)
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    maxFiles: 20,
    disabled: loading || uploadingImages
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = formData.fotos.indexOf(active.id);
      const newIndex = formData.fotos.indexOf(over.id);
      setFormData(prev => ({
        ...prev,
        fotos: arrayMove(prev.fotos, oldIndex, newIndex)
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.marca) newErrors.marca = 'Marca es requerida';
    if (!formData.modelo) newErrors.modelo = 'Modelo es requerido';
    if (!formData.anio) newErrors.anio = 'Año es requerido';
    if (!formData.km) newErrors.km = 'Kilómetros es requerido';
    if (!formData.tipoCombustible) newErrors.tipoCombustible = 'Tipo de combustible es requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    if (formData.fotos.length < 10) {
      toast.error('Se requieren mínimo 10 fotos');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    const newErrors = {};
    if (!formData.precioBase) newErrors.precioBase = 'Precio base es requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    let valid = true;
    if (step === 'step1') valid = validateStep1();
    if (step === 'step2') valid = validateStep2();
    if (step === 'step4') valid = validateStep4();

    if (!valid) return;

    const steps = ['step1', 'step2', 'step3', 'step4', 'step5'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    const steps = ['step1', 'step2', 'step3', 'step4', 'step5'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };

  const handleSaveDraft = async () => {
    try {
      const payload = {
        ...formData,
        anio: parseInt(formData.anio),
        km: parseInt(formData.km),
        precioBase: parseFloat(formData.precioBase || 0),
        estadoPublicacion: 'Borrador',
        fechaLimiteOfertas: formData.fechaLimiteOfertas ? new Date(formData.fechaLimiteOfertas).toISOString() : null,
      };

      // Remove empty sucursalId
      if (!payload.sucursalId) delete payload.sucursalId;

      if (editId) {
        // Actualizar borrador existente
        await axios.put(`${API}/vehiculos/${editId}`, payload);
        toast.success('Borrador actualizado');
      } else {
        // Crear nuevo borrador
        await axios.post(`${API}/vehiculos`, payload);
        toast.success('Borrador guardado');
      }

      navigate('/admin/publicaciones');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(editId ? 'Error al actualizar borrador' : 'Error al guardar borrador');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!validateStep1() || !validateStep2() || !validateStep4()) {
        toast.error('Por favor completa todos los campos requeridos');
        return;
      }

      const payload = {
        ...formData,
        anio: parseInt(formData.anio),
        km: parseInt(formData.km),
        precioBase: parseFloat(formData.precioBase),
        estadoPublicacion: 'Enviada',
        fechaLimiteOfertas: formData.fechaLimiteOfertas ? new Date(formData.fechaLimiteOfertas).toISOString() : null,
      };

      // Remove empty sucursalId
      if (!payload.sucursalId) delete payload.sucursalId;

      if (editId) {
        // Actualizar vehículo existente
        await axios.put(`${API}/vehiculos/${editId}`, payload);
        toast.success('Publicación actualizada y enviada');
      } else {
        // Crear nuevo vehículo
        await axios.post(`${API}/vehiculos`, payload);
        toast.success('Publicación creada y enviada');
      }

      navigate('/admin/publicaciones');
    } catch (error) {
      console.error('Error saving publication:', error);
      toast.error(editId ? 'Error al actualizar publicación' : 'Error al crear publicación');
    }
  };

  const progressValue = {
    step1: 20,
    step2: 40,
    step3: 60,
    step4: 80,
    step5: 100,
  }[step];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando datos...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">
            {editId ? 'Editar Publicación' : duplicarId ? 'Duplicar Publicación' : 'Nueva Publicación'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {editId ? 'Modifica los datos de tu publicación' : duplicarId ? 'Revisá los datos y guardá como nueva publicación' : 'Completa los 5 pasos para crear una publicación'}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Progress value={progressValue} className="h-2 mb-6" />

        <Tabs value={step} onValueChange={setStep} className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="step1">Básicos</TabsTrigger>
            <TabsTrigger value="step2">Fotos</TabsTrigger>
            <TabsTrigger value="step3">Detalles</TabsTrigger>
            <TabsTrigger value="step4">Precio</TabsTrigger>
            <TabsTrigger value="step5">Revisión</TabsTrigger>
          </TabsList>

          {/* Step 1: Datos Básicos */}
          <TabsContent value="step1" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Datos Básicos del Vehículo</h2>
              <div className="grid gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="marca">Marca *</Label>
                    <Input
                      id="marca"
                      value={formData.marca}
                      onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                      data-testid="marca-input"
                    />
                    {errors.marca && (
                      <p className="text-sm text-destructive">{errors.marca}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modelo">Modelo *</Label>
                    <Input
                      id="modelo"
                      value={formData.modelo}
                      onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                      data-testid="modelo-input"
                    />
                    {errors.modelo && (
                      <p className="text-sm text-destructive">{errors.modelo}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="version">Versión</Label>
                  <Input
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="anio">Año *</Label>
                    <Select
                      value={formData.anio.toString()}
                      onValueChange={(value) => setFormData({ ...formData, anio: parseInt(value) })}
                    >
                      <SelectTrigger id="anio" data-testid="anio-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="km">Kilómetros *</Label>
                    <Input
                      id="km"
                      type="number"
                      value={formData.km}
                      onChange={(e) => setFormData({ ...formData, km: e.target.value })}
                      data-testid="km-input"
                    />
                    {errors.km && (
                      <p className="text-sm text-destructive">{errors.km}</p>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tipoCombustible">Tipo de Combustible *</Label>
                    <Select
                      value={formData.tipoCombustible}
                      onValueChange={(value) =>
                        setFormData({ ...formData, tipoCombustible: value })
                      }
                    >
                      <SelectTrigger id="tipoCombustible" data-testid="combustible-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Nafta">Nafta</SelectItem>
                        <SelectItem value="Diesel">Diesel</SelectItem>
                        <SelectItem value="Híbrido">Híbrido</SelectItem>
                        <SelectItem value="Eléctrico">Eléctrico</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.tipoCombustible && (
                      <p className="text-sm text-destructive">{errors.tipoCombustible}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cilindrada">Cilindrada</Label>
                    <Input
                      id="cilindrada"
                      placeholder="ej. 1.6L"
                      value={formData.cilindrada}
                      onChange={(e) => setFormData({ ...formData, cilindrada: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comentarios">Comentarios</Label>
                  <Textarea
                    id="comentarios"
                    rows={4}
                    value={formData.comentarios}
                    onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                    data-testid="comentarios-textarea"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Step 2: Fotos */}
          <TabsContent value="step2" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-2">Fotos del Vehículo</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Mínimo 10 fotos, máximo 20. Sigue la guía para mejor presentación.
              </p>

              {formData.fotos.length < 10 && (
                <Alert className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Se requieren al menos 10 fotos para continuar ({formData.fotos.length}/10)
                  </AlertDescription>
                </Alert>
              )}

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-md p-8 text-center transition-colors cursor-pointer ${isDragActive ? 'bg-accent/60 border-primary' : 'bg-accent/30 border-border'
                  } ${uploadingImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="photo-upload-dropzone"
              >
                <input {...getInputProps()} />
                {uploadingImages ? (
                  <div className="flex flex-col items-center gap-2">
                    {/* Simple spinner using CSS borders */}
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent text-primary" />
                    <p className="text-sm text-muted-foreground">Subiendo imágenes...</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {isDragActive
                        ? 'Suelta las fotos aquí...'
                        : 'Arrastra y suelta hasta 20 fotos (JPG/PNG/WEBP, máx 10MB c/u)'}
                    </p>
                    <Button variant="secondary" className="mt-3" type="button">
                      Seleccionar archivos
                    </Button>
                  </>
                )}
              </div>

              {formData.fotos.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-medium mb-3">Fotos cargadas ({formData.fotos.length}) — <span className="text-sm font-normal text-muted-foreground">arrastrá para reordenar</span></h3>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={formData.fotos} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {formData.fotos.map((url, i) => (
                          <SortablePhoto key={url} url={url} index={i} onRemove={removePhoto} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              <div className="mt-6 p-4 bg-muted rounded-md">
                <h4 className="font-medium mb-2 text-sm">Guía de fotos obligatorias (1-10):</h4>
                <div className="grid sm:grid-cols-2 gap-1 text-sm text-muted-foreground">
                  {photoGuide.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      {formData.fotos.length >= parseInt(item) && (
                        <Check className="h-3 w-3 text-green-600" />
                      )}
                      {item}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Fotos 11-20 son opcionales para detalles adicionales
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* Step 3: Detalles Extra */}
          <TabsContent value="step3" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Detalles Adicionales</h2>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estadoGeneral">Estado General</Label>
                  <Select
                    value={formData.estadoGeneral}
                    onValueChange={(value) => setFormData({ ...formData, estadoGeneral: value })}
                  >
                    <SelectTrigger id="estadoGeneral">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Excelente">Excelente</SelectItem>
                      <SelectItem value="Muy Bueno">Muy bueno</SelectItem>
                      <SelectItem value="Bueno">Bueno</SelectItem>
                      <SelectItem value="Regular">Regular</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="unicoDueno"
                    checked={formData.unicoDueno}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, unicoDueno: checked })
                    }
                    data-testid="unico-dueno-checkbox"
                  />
                  <Label htmlFor="unicoDueno" className="cursor-pointer">
                    Único dueño
                  </Label>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Step 4: Precio */}
          <TabsContent value="step4" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Precio y Condiciones de Oferta</h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="precioBase">Precio Base (USD) *</Label>
                  <Input
                    id="precioBase"
                    type="number"
                    placeholder="15000"
                    value={formData.precioBase}
                    onChange={(e) => setFormData({ ...formData, precioBase: e.target.value })}
                    data-testid="price-input"
                  />
                  {errors.precioBase && (
                    <p className="text-sm text-destructive">{errors.precioBase}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Este será el precio de referencia para las ofertas de los revendedores
                  </p>
                </div>

                {/* Sucursal */}
                {sucursales.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="sucursal">Sucursal</Label>
                    <Select
                      value={formData.sucursalId || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, sucursalId: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="sucursal-select">
                        <SelectValue placeholder="Seleccionar sucursal..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin sucursal asignada</SelectItem>
                        {sucursales.map((sucursal) => (
                          <SelectItem key={sucursal.id} value={sucursal.id}>
                            {sucursal.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                      La sucursal desde donde se publica el vehículo
                    </p>
                  </div>
                )}

                {/* Subasta */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <Gavel className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="esSubasta"
                          checked={formData.esSubasta}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, esSubasta: checked })
                          }
                          data-testid="es-subasta-checkbox"
                        />
                        <Label htmlFor="esSubasta" className="cursor-pointer font-medium">
                          Es subasta
                        </Label>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Activa el modo subasta donde los revendedores compiten con ofertas
                      </p>
                    </div>
                  </div>
                </div>

                {/* Fecha límite */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="fechaLimite" className="font-medium">Fecha límite de ofertas</Label>
                      <Input
                        id="fechaLimite"
                        type="datetime-local"
                        value={formData.fechaLimiteOfertas}
                        onChange={(e) => setFormData({ ...formData, fechaLimiteOfertas: e.target.value })}
                        data-testid="fecha-limite-input"
                      />
                      <p className="text-sm text-muted-foreground">
                        Las ofertas solo se podrán recibir hasta esta fecha y hora
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Step 5: Revisión */}
          <TabsContent value="step5" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Revisión Final</h2>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Marca:</span>{' '}
                    <span className="font-medium">{formData.marca}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Modelo:</span>{' '}
                    <span className="font-medium">{formData.modelo}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Año:</span>{' '}
                    <span className="font-medium">{formData.anio}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Kilómetros:</span>{' '}
                    <span className="font-medium">{parseInt(formData.km || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Combustible:</span>{' '}
                    <span className="font-medium">{formData.tipoCombustible}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Precio Base:</span>{' '}
                    <span className="font-medium text-primary">
                      ${parseFloat(formData.precioBase || 0).toLocaleString('en-US')}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Fotos:</span>{' '}
                    <span className="font-medium">{formData.fotos.length}</span>
                  </div>
                  {formData.estadoGeneral && (
                    <div>
                      <span className="text-muted-foreground">Estado:</span>{' '}
                      <span className="font-medium">{formData.estadoGeneral}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Subasta:</span>{' '}
                    <span className="font-medium">{formData.esSubasta ? 'Sí' : 'No'}</span>
                  </div>
                  {formData.fechaLimiteOfertas && (
                    <div>
                      <span className="text-muted-foreground">Fecha límite:</span>{' '}
                      <span className="font-medium">
                        {new Date(formData.fechaLimiteOfertas).toLocaleString('es-UY')}
                      </span>
                    </div>
                  )}
                  {formData.sucursalId && (
                    <div>
                      <span className="text-muted-foreground">Sucursal:</span>{' '}
                      <span className="font-medium">
                        {sucursales.find(s => s.id === formData.sucursalId)?.nombre || '-'}
                      </span>
                    </div>
                  )}
                </div>

                {formData.comentarios && (
                  <div>
                    <span className="text-sm text-muted-foreground">Comentarios:</span>
                    <p className="text-sm mt-1">{formData.comentarios}</p>
                  </div>
                )}

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Al confirmar el envío, la publicación será visible para los revendedores y podrán
                    realizar ofertas.
                  </AlertDescription>
                </Alert>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center py-6">
          <div>
            {step !== 'step1' && (
              <Button
                variant="outline"
                onClick={handlePrev}
                data-testid="wizard-prev-button"
              >
                Anterior
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              data-testid="save-draft-button"
            >
              Guardar Borrador
            </Button>
            {step !== 'step5' ? (
              <Button onClick={handleNext} data-testid="wizard-next-button">
                Siguiente
              </Button>
            ) : (
              <Button onClick={handleSubmit} data-testid="submit-listing-button">
                Confirmar Envío
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NuevaPublicacion;
